import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { RequestService } from 'src/app/services/request.service';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { Chart } from 'chart.js/auto';

declare var google: any;

@Component({
  selector: 'app-single-meteo',
  templateUrl: './single-meteo.component.html',
  styleUrls: ['./single-meteo.component.scss']
})
export class SingleMeteoComponent implements OnInit, AfterViewInit {

  @ViewChild('lineCanvas') private lineCanvas!: ElementRef;

  station: any = [];
  stationID = '';
  selectedTimeRange = 'day';
  name = '';
  latest: string | null = '';
  items = [
    'METEO.HUMIDITY', 'METEO.BAR', 'METEO.TEMP', 'METEO.RAIN_24', 'METEO.RAIN_DAILY', 'METEO.RAIN_RATE',
    'METEO.RAIN_WEEK', 'METEO.RAIN_MONTH', 'METEO.RAIN_YEAR', 'METEO.WIND_SPEED', 'METEO.WIND_DIR', 'METEO.WIND_GUST_DIR'
  ];
  itemValues: any = {};
  lineChart: any;
  loading = false;
  routerUrl = '';
  weatherApi = false;
  plots: any[] = [];
  selectedPlotCode = '';

  constructor(
    private router: Router,
    private req: RequestService<any>,
    public translate: TranslateService,
    private datepipe: DatePipe
  ) { }

  ngOnInit() {
    this.getPlots();
  }

  ngAfterViewInit() {
    this.initChart();
    this.weatherApi ? this.onTimeRangeChange() : this.populateChart();
  }

  getPlots() {
    this.selectedTimeRange = 'day';
    this.req.getPlots().subscribe(res => {
      this.plots = res;
      this.selectedPlotCode = res[0].plot_code;
      this.routerUrl = this.router.url;
      this.weatherApi = this.routerUrl.includes('WeatherData');
      if (!this.weatherApi) {
        this.stationID = this.routerUrl.split('/station-single/')[1];
        this.req.getSingleStation(this.stationID).subscribe((res) => {
          this.name = res[0].station_name;
          this.initMap();
        });
        this.req.getStationMeteorologicalData(this.stationID).subscribe((res) => this.weatherData(res));
      }
      this.getSelectedPlotData();
    });
  }

  getSelectedPlotData() {
    const selected = this.plots.find(plot => plot.plot_code === this.selectedPlotCode);
    if (!selected) return;
    this.initMapWeather(selected);
    this.req.getMeteoChart(this.selectedPlotCode, this.selectedTimeRange).subscribe((res) => {
      this.updateChartData(res);
      this.req.getMeteo(this.selectedPlotCode).subscribe((res) => this.weatherData(res));
    });
  }

  weatherData(res: any) {
    this.latest = this.datepipe.transform(res.last_updated, 'medium');
    this.itemValues = {
      'METEO.HUMIDITY': res.hum_out + ' %',
      'METEO.BAR': res.bar_absolute + ' mb',
      'METEO.TEMP': res.temp_out + ' °C',
      'METEO.RAIN_24': res.rain_24_hr_mm + ' mm',
      'METEO.RAIN_DAILY': res.rain_day_mm + ' mm',
      'METEO.RAIN_RATE': res.rain_rate_mm + ' mm',
      'METEO.RAIN_WEEK': res.rain_week_mm + ' mm',
      'METEO.RAIN_MONTH': res.rain_month_mm + ' mm',
      'METEO.RAIN_YEAR': res.rain_year_mm + ' mm',
      'METEO.WIND_SPEED': res.wind_speed + ' km/h',
      'METEO.WIND_DIR': res.wind_dir + ' ° ' + this.getWindDirection(res.wind_dir),
      'METEO.WIND_GUST_DIR': res.wind_dir_of_gust_10_min + ' ° ' + this.getWindDirection(res.wind_dir_of_gust_10_min)
    };
  }

  getWindDirection(degree: number): string {
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    return directions[Math.round(degree / 22.5) % 16];
  }

  updateChartData(res: any[]) {
    this.loading = true;
    const labels = res.map(d =>
      new Date(d.timestamp).toLocaleString('el-GR', { timeZone: 'Europe/Athens' })
    );    
    const getData = (key: keyof typeof res[0]) => res.map(d => d[key]);
    this.lineChart.data.labels = labels;
    this.lineChart.data.datasets[0].data = getData('bar_absolute');
    this.lineChart.data.datasets[1].data = getData('temp_out');
    this.lineChart.data.datasets[2].data = getData('hum_out');
    this.lineChart.data.datasets[3].data = getData('rain_rate_hi_mm');
    this.lineChart.data.datasets[4].data = getData('rainfall_mm');
    this.lineChart.data.datasets[5].data = getData('wind_speed_hi');
    this.lineChart.update();
    this.loading = false;
  }

  onTimeRangeChange() {
    if (this.selectedTimeRange.includes('forecast')) {
      this.req.getMeteoForecast().subscribe((res) => {
        this.updateChartData(res)
      });
    } else if (this.weatherApi) {
      this.req.getMeteoChart(this.selectedPlotCode, this.selectedTimeRange).subscribe((res) => this.updateChartData(res));
    } else {
      this.populateChart();
    }
  }

  populateChart() {
    this.loading = true;
    this.req.getStationHistoricalData(this.stationID, this.selectedTimeRange).subscribe((res) => this.updateChartData(res));
  }

  initChart() {
    this.lineChart = new Chart(this.lineCanvas.nativeElement, {
      type: 'line',
      data: { labels: [], datasets: this.getDatasetsConfig() },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: { ticks: { font: { size: 8, family: 'Arial' } }, grid: { color: 'lightgray', lineWidth: 0.1 } },
          y: { ticks: { font: { size: 8, family: 'Arial' }, color: 'grey' }, grid: { color: 'lightgray', lineWidth: 0.3 } }
        }
      }
    });
  }

  getDatasetsConfig() {
    const colors = [
      ['Air Pressure (mb)', '#49d7ff', 'rgba(99, 216, 248, 0.455)', '#1f90ec'],
      ['Temperature (°C)', '#ff6384', 'rgba(255, 159, 160, 0.5)', '#ff6384'],
      ['Humidity (%)', '#ff9f40', 'rgba(255, 205, 86, 0.5)', '#ff9f40'],
      ['Rainfall Rate (mm)', '#ff49aa', 'rgba(244, 108, 181, 0.307)', '#f60aee'],
      ['Rainfall (mm)', '#ff49aa', 'rgba(245, 172, 255, 0.553)', '#f60aee'],
      ['Wind Speed (km/h)', '#43a047', 'rgba(129, 199, 132, 0.5)', '#43a047']
    ];
    return colors.map(([label, border, background, pointBorder]) => ({
      label,
      data: [],
      fill: true,
      borderColor: border,
      backgroundColor: background,
      borderWidth: 1,
      tension: 0.4,
      pointBackgroundColor: 'white',
      pointBorderWidth: 1,
      pointBorderColor: pointBorder
    }));
  }

  initMapWeather(res: any) {
    this.station = [res];
    this.renderMap(
      res.map_coordinates[0].lat,
      res.map_coordinates[0].lon,
      this.getWeatherInfoWindowContent(res),
      res.markerIcon
    );
  }

  initMap() {
    this.req.getWeatherStations().subscribe(res => {
      const st = res.find(el => el.station_id == this.stationID);
      if (st) {
        this.station = [st];
        this.renderMap(st.latitude, st.longitude, this.getStationInfoWindowContent(st), st.markerIcon);
      }
    });
  }

  renderMap(lat: number, lng: number, content: string, icon: string) {
    const map = new google.maps.Map(document.getElementById("map")!, {
      zoom: 15,
      center: { lat, lng },
      mapTypeId: google.maps.MapTypeId.SATELLITE,
      mapTypeControl: false,
      streetViewControl: false
    });
    const marker = new google.maps.Marker({ position: { lat, lng }, map, icon, title: 'Sensor Marker' });
    const infoWindow = new google.maps.InfoWindow({ content });
    marker.addListener('click', () => infoWindow.open(map, marker));
  }

  getWeatherInfoWindowContent(res: any): string {
    return `
      <div class="info-window-content">
        <h3>${this.name}</h3>
        <mat-list>
          <mat-list-item>Status: ${res.crop_info[0].unharvested ? 'Active' : 'Inactive'}</mat-list-item>
          <mat-list-item>Crop Type: ${res.crop_info[0].crop_type}</mat-list-item>
          <mat-list-item>Registered: ${this.datepipe.transform(res.crop_info[0].crop_contract_date)}</mat-list-item>
        </mat-list>
      </div>
    `;
  }

  getStationInfoWindowContent(station: any): string {
    return `
      <div class="info-window-content">
        <h3>${this.name}</h3>
        <mat-list>
          <mat-list-item>Status: ${station.active ? 'Active' : 'Inactive'}</mat-list-item>
          <mat-list-item>Company Name: ${station.company_name}</mat-list-item>
          <mat-list-item>Registered: ${this.datepipe.transform(station.registered_date)}</mat-list-item>
        </mat-list>
      </div>
    `;
  }
}
